import { ReactNode } from "react";
import {
  Drawer,
  Box,
  Switch,
  FormControlLabel,
  IconButton,
  Typography,
  Link,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import PublicIcon from "@mui/icons-material/Public";
import TimelineIcon from "@mui/icons-material/Timeline";
import SatelliteAltOutlinedIcon from "@mui/icons-material/SatelliteAltOutlined";
import { useAppSettings } from "../../context/AppSettingsContext";

type SettingItemProps = {
  icon: ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

const SettingItem = ({
  icon,
  label,
  description,
  checked,
  onChange,
}: SettingItemProps) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1.5 }}>
    <Box sx={{ color: "text.secondary", display: "flex" }}>{icon}</Box>
    <FormControlLabel
      labelPlacement="start"
      sx={{
        flex: 1,
        m: 0,
        justifyContent: "space-between",
        gap: 1,
        ".MuiFormControlLabel-label": { minWidth: 0 },
      }}
      control={
        <Switch
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          inputProps={{ "aria-label": label }}
        />
      }
      label={
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              display: "block",
              mt: 0.25,
              lineHeight: 1.5,
            }}
          >
            {description}
          </Typography>
        </Box>
      }
    />
  </Box>
);

export const AppDrawer = () => {
  const {
    showWaffleIcon,
    setShowWaffleIcon,
    isDarkMode,
    setIsDarkMode,
    renderAlternateMap,
    setRenderAlternateMap,
    showSatTrails,
    setShowSatTrails,
    drawerOpen,
    setDrawerOpen,
  } = useAppSettings();
  const mapView = renderAlternateMap ? "map" : "globe";

  return (
    <Drawer
      variant="temporary"
      anchor="left"
      open={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      slotProps={{
        paper: {
          id: "map-settings",
          "aria-label": "Map settings",
          sx: {
            width: 320,
            maxWidth: "calc(100vw - 24px)",
            borderRight: 1,
            borderColor: "divider",
            pt: "env(safe-area-inset-top, 0px)",
            pb: "env(safe-area-inset-bottom, 0px)",
          },
        },
      }}
    >
      <Box
        sx={{
          px: 2.5,
          py: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Box>
          <Typography component="h2" sx={{ fontSize: 18, fontWeight: 650 }}>
            Map settings
          </Typography>
        </Box>
        <IconButton
          onClick={() => setDrawerOpen(false)}
          aria-label="Close map settings"
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          px: 2.5,
          py: 3,
        }}
      >
        <Box>
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.08em",
            }}
          >
            View
          </Typography>
          <ToggleButtonGroup
            value={mapView}
            exclusive
            fullWidth
            onChange={(_event, value: string | null) => {
              if (value !== null) setRenderAlternateMap(value === "map");
            }}
            aria-label="Map view"
            sx={{
              mt: 1,
              mb: 3,
              ".MuiToggleButton-root": {
                minHeight: 44,
                gap: 1,
                color: "text.secondary",
                borderColor: "divider",
                "&.Mui-selected": {
                  color: "primary.main",
                  bgcolor: "action.selected",
                },
              },
            }}
          >
            <ToggleButton value="globe">
              <PublicIcon sx={{ fontSize: 18 }} />
              Globe
            </ToggleButton>
            <ToggleButton value="map">
              <MapOutlinedIcon sx={{ fontSize: 18 }} />
              Map
            </ToggleButton>
          </ToggleButtonGroup>
          <Typography
            variant="overline"
            sx={{
              color: "text.secondary",
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: "0.08em",
            }}
          >
            Layers
          </Typography>
          {!renderAlternateMap && (
            <SettingItem
              icon={<TimelineIcon fontSize="small" />}
              label="Orbit trails"
              description="Show the next 45 minutes of each orbit."
              checked={showSatTrails}
              onChange={setShowSatTrails}
            />
          )}
          {renderAlternateMap && (
            <SettingItem
              icon={<SatelliteAltOutlinedIcon fontSize="small" />}
              label="Satellite artwork"
              description="Show detailed array markers on the map."
              checked={showWaffleIcon}
              onChange={setShowWaffleIcon}
            />
          )}
          <Box sx={{ mt: 3 }}>
            <Typography
              variant="overline"
              sx={{
                color: "text.secondary",
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: "0.08em",
              }}
            >
              Appearance
            </Typography>
            <SettingItem
              icon={<DarkModeOutlinedIcon fontSize="small" />}
              label="Dark mode"
              description="Use a darker interface around the map."
              checked={isDarkMode}
              onChange={setIsDarkMode}
            />
          </Box>
        </Box>
        <Box
          component="section"
          aria-labelledby="support-project-title"
          sx={{ mt: "auto", pt: 3 }}
        >
          <Box sx={{ borderTop: 1, borderColor: "divider", pt: 2 }}>
            <Typography
              id="support-project-title"
              component="h3"
              sx={{ fontSize: 13, fontWeight: 600 }}
            >
              Support the project
            </Typography>
            <Link
              href="https://www.insightanalytics.io/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Visit Insight Analytics (opens in a new tab)"
              underline="none"
              color="text.primary"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                minHeight: 44,
                my: 0.5,
                borderRadius: 0.5,
                "&:hover": { color: "primary.main" },
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 3,
                },
              }}
            >
              <Box
                component="img"
                src="/insight-analytics-mark.svg"
                alt=""
                width={30}
                height={26}
                sx={{ objectFit: "contain" }}
              />
              <Typography
                component="span"
                sx={{ fontSize: 14, fontWeight: 600 }}
              >
                Insight Analytics
              </Typography>
              <OpenInNewRoundedIcon
                sx={{ fontSize: 14, color: "text.secondary" }}
              />
            </Link>
            <Typography
              sx={{ fontSize: 12, lineHeight: 1.6, color: "text.secondary" }}
            >
              I work full time on Insight Analytics, my stock research platform.
              Any support would mean a lot.
            </Typography>
            <Typography
              sx={{
                mt: 1.25,
                fontSize: 12,
                lineHeight: 1.7,
                color: "text.secondary",
              }}
            >
              Spacemob members get 50% off for life.
              <br />
              Use code{" "}
              <Box
                component="code"
                sx={{
                  px: 0.5,
                  py: 0.25,
                  borderRadius: 0.5,
                  bgcolor: "action.hover",
                  color: "text.primary",
                  fontWeight: 600,
                  userSelect: "all",
                }}
              >
                SPACEMOB50
              </Box>{" "}
              at checkout.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};
